<template>
  <v-container>
    <v-layout>
      <v-flex xs12 sm8 offset-sm2>
        <div class="registration-page iabf-dancer-theme">
          <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>

          <h1>You’re in!</h1>
          <p class="subtitle">
            Here’s what’s waiting for you at the festival.
          </p>

          <section class="package-hero iabf-card">
            <p class="package-kicker">Your package</p>
            <h2 class="package-name">{{ packageName }}</h2>
            <p v-if="lodgingLine" class="package-lodging">{{ lodgingLine }}</p>
            <p v-if="!room" class="package-hint">
              We’re still sorting your room. Email
              <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> if something looks off.
            </p>
          </section>

          <!-- Returning free gifts — above package includes so they pick early -->
          <section v-if="freeGiftCount > 0" class="gifts-section">
            <h2 class="section-title">Your free gifts</h2>
            <p class="gifts-lede">
              Thank you for coming back!
              As a returning dancer (year {{ attendanceYearNumber }}), you get
              <strong>{{ freeGiftCountLabel }}</strong>.
              Pick {{ freeGiftCount === 1 ? 'your gift' : 'your gifts' }} below
              — you can choose the same gift more than once.
            </p>
            <p class="gifts-policy">
              You can change an unused gift anytime. Once a gift is applied
              (free carpool, merch credit, guest ticket, payment discount, or photoshoot credit),
              that gift stays locked.
              Need a change? Email
              <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
            </p>

            <registration-gift-picker
              class="iabf-card"
              id-prefix="gift-slot-top-"
              :gift-slots="giftSlots"
              :gift-lock-flags="giftLockFlags"
              :options="giftOptions"
              :gift-error="giftError"
              :gift-success="giftSuccess"
              :gift-saving="giftSaving"
              :gifts-dirty="giftsDirty"
              @select="onGiftSelect"
              @save="saveGifts"
            />
          </section>

          <section class="includes-section">
            <h2 class="section-title">What’s included</h2>
            <div
              v-for="(group, gi) in includeGroups"
              :key="'g-' + gi"
              class="includes-group"
            >
              <h3 class="includes-group-title">{{ group.title }}</h3>
              <ul class="includes-list">
                <li
                  v-for="(row, i) in group.items"
                  :key="gi + '-' + i"
                  class="includes-row"
                  :class="{ 'is-excluded': !row.included }"
                >
                  <span class="includes-mark" aria-hidden="true">{{ row.included ? '✓' : '×' }}</span>
                  <span class="includes-body">
                    <span class="includes-label">{{ row.label }}</span>
                    <span v-if="row.detail" class="includes-detail">{{ row.detail }}</span>
                  </span>
                </li>
              </ul>
              <registration-gift-picker
                v-if="group.title === 'Thank-you gifts' && freeGiftCount > 0"
                nested
                id-prefix="gift-slot-includes-"
                :gift-slots="giftSlots"
                :gift-lock-flags="giftLockFlags"
                :options="giftOptions"
                :gift-error="giftError"
                :gift-success="giftSuccess"
                :gift-saving="giftSaving"
                :gifts-dirty="giftsDirty"
                @select="onGiftSelect"
                @save="saveGifts"
              />
            </div>
          </section>

          <p class="footer-hint">
            Questions? We’re happy to help —
            <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
          </p>

          <section class="related-links">
            <h2 class="section-title">Next stops</h2>
            <ul class="link-list">
              <li><router-link to="/lodging">How you’re getting here →</router-link></li>
              <li><router-link to="/pieces">Your showcase pieces →</router-link></li>
              <li><router-link to="/billing">Payments →</router-link></li>
            </ul>
          </section>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import {
  packageNameFromRoom,
  lodgingLineWithRoomNumber,
  registrationIncludesGrouped
} from '@/utils/iabf-registration-package'
import {
  REGISTRATION_GIFT_OPTIONS,
  freeGiftCountFromYearsAttended,
  freeGiftCountLabel,
  detectGiftRedemptions,
  lockFlagsForGiftSlots,
  validateGiftSelectionsAgainstRedemptions
} from '@/config/iabf-gifts'
import { IABF } from '@/config/iabf-collections'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import { yearsAttendedForDancer } from '@/utils/iabf-attendance-years'
import { saveErrorMessage } from '@/utils/save-error'
import { db } from '@/firebase'
import RegistrationGiftPicker from '@/components/iabf/RegistrationGiftPicker.vue'

export default {
  name: 'IABFRegistration',
  components: { RegistrationGiftPicker },
  data () {
    return {
      giftSlots: [],
      giftsDirty: false,
      giftSaving: false,
      giftError: '',
      giftSuccess: '',
      giftContextLoading: false,
      addonServicesForGifts: []
    }
  },
  computed: {
    user () {
      return this.$store.getters.user
    },
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    person () {
      return this.$store.getters.currentIABFPerson || {}
    },
    room () {
      return (this.currentIABFDancer && this.currentIABFDancer.room) || ''
    },
    packageName () {
      return packageNameFromRoom(this.room)
    },
    lodgingLine () {
      if (!this.room) {
        return ''
      }
      const d = this.currentIABFDancer || {}
      return lodgingLineWithRoomNumber(this.room, d.roomNumber)
    },
    includeGroups () {
      return registrationIncludesGrouped(this.currentIABFDancer || {})
    },
    giftOptions () {
      return REGISTRATION_GIFT_OPTIONS
    },
    festivalYear () {
      // Enrollment year on this dancer — never the admin year switcher.
      return Number(this.currentIABFDancer && this.currentIABFDancer.festivalYear) ||
        Number(this.$store.getters.iabfDancerYear) ||
        IABF_DANCER_YEAR
    },
    yearsAttended () {
      return yearsAttendedForDancer(
        this.person,
        this.currentIABFDancer || {},
        this.festivalYear
      )
    },
    freeGiftCount () {
      return freeGiftCountFromYearsAttended(this.yearsAttended, this.festivalYear)
    },
    freeGiftCountLabel () {
      return freeGiftCountLabel(this.freeGiftCount)
    },
    attendanceYearNumber () {
      const set = new Set((this.yearsAttended || []).map(Number).filter(n => n > 0))
      set.add(this.festivalYear)
      return set.size
    },
    giftRedemptions () {
      return detectGiftRedemptions({
        dancer: this.currentIABFDancer,
        carpoolSlots: this.$store.getters.iabfCarpoolSlots || [],
        guestRegistrations: this.$store.getters.guestRegistrations || [],
        addonServices: this.addonServicesForGifts
      })
    },
    giftLockFlags () {
      return lockFlagsForGiftSlots(this.giftSlots, this.giftRedemptions)
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.initDancer()
    if (this.$store.state.masqueradeDancerId) {
      this.$store.dispatch('refreshEffectiveDancer').catch(() => {})
    }
  },
  watch: {
    user (val) {
      if (val) {
        this.initDancer()
      }
    },
    currentIABFDancer: {
      immediate: true,
      handler (d) {
        if (d && d.id) {
          this.syncGiftSlotsFromDancer()
          this.loadGiftRedemptionContext(d)
        }
      }
    },
    freeGiftCount () {
      this.syncGiftSlotsFromDancer()
    }
  },
  methods: {
    initDancer () {
      if (this.currentIABFDancer && this.currentIABFDancer.id) {
        return
      }
      if (!this.user) {
        return
      }
      const uid = this.user.id
      const email = this.user.email
      this.$store.dispatch('loadIABFDancerByUid', uid).then(() => {
        if (this.currentIABFDancer && this.currentIABFDancer.id) {
          return
        }
        if (email) {
          this.$store.dispatch('linkIABFDancerByEmail', { email, uid })
        }
      })
    },
    async loadGiftRedemptionContext (dancer) {
      if (!dancer || !dancer.id) {
        return
      }
      this.giftContextLoading = true
      const email = (dancer.email || '').toLowerCase()
      try {
        await Promise.all([
          this.$store.dispatch('loadCarpoolSlots').catch(() => {}),
          email
            ? this.$store.dispatch('loadGuestRegistrationsForDancer', email).catch(() => {})
            : Promise.resolve(),
          // Photoshoot / carpool addon gift credits (if any).
          db.collection(IABF.addonServices)
            .where('dancerId', '==', dancer.id)
            .get()
            .then(snap => {
              this.addonServicesForGifts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            })
            .catch(() => {
              this.addonServicesForGifts = []
            })
        ])
      } finally {
        this.giftContextLoading = false
      }
    },
    syncGiftSlotsFromDancer () {
      if (this.giftsDirty) {
        // Don't clobber in-progress edits when other fields refresh.
        return
      }
      const d = this.currentIABFDancer || {}
      const n = this.freeGiftCount
      const slots = []
      for (let i = 1; i <= n; i++) {
        const val = d[`registrationGift${i}`]
        // Empty / None → prompt to choose (returning dancers earn these)
        if (!val || val === 'None') {
          slots.push('Not Yet Selected')
        } else {
          slots.push(val)
        }
      }
      this.giftSlots = slots
      this.giftError = ''
      this.giftSuccess = ''
    },
    optionsForSlot () {
      // Same gift may be chosen in multiple slots (e.g. two banquet tickets).
      return REGISTRATION_GIFT_OPTIONS
    },
    onGiftSelect (idx, event) {
      const nextVal = event && event.target ? event.target.value : ''
      const prev = this.giftSlots[idx]
      if (this.giftLockFlags[idx] && this.giftLockFlags[idx].locked) {
        // Disabled selects shouldn't fire, but restore just in case.
        event.target.value = prev
        return
      }
      const nextSlots = this.giftSlots.slice()
      nextSlots[idx] = nextVal
      const block = validateGiftSelectionsAgainstRedemptions(nextSlots, this.giftRedemptions)
      if (block) {
        event.target.value = prev
        this.giftError = block
        this.giftSuccess = ''
        return
      }
      this.$set(this.giftSlots, idx, nextVal)
      this.giftsDirty = true
      this.giftError = ''
      this.giftSuccess = ''
    },
    async saveGifts () {
      const d = this.currentIABFDancer
      if (!d || !d.id) {
        this.giftError = 'Hang tight — your registration is still loading.'
        return
      }
      const block = validateGiftSelectionsAgainstRedemptions(this.giftSlots, this.giftRedemptions)
      if (block) {
        this.giftError = block
        return
      }
      const n = this.freeGiftCount
      const payload = {
        id: d.id,
        registrationGift1: 'None',
        registrationGift2: 'None',
        registrationGift3: 'None',
        registrationGift4: 'None'
      }
      for (let i = 0; i < n; i++) {
        const g = this.giftSlots[i] || 'Not Yet Selected'
        payload[`registrationGift${i + 1}`] = g
      }
      this.giftSaving = true
      this.giftError = ''
      try {
        await this.$store.dispatch('updateRegistrationGifts', payload)
        this.giftsDirty = false
        this.giftSuccess = 'Gifts saved — thank you!'
      } catch (e) {
        this.giftError = saveErrorMessage(e)
      } finally {
        this.giftSaving = false
      }
    }
  }
}
</script>

<style scoped>
.registration-page {
  max-width: 560px;
  margin: 0 auto;
  padding: 40px 20px 60px;
  color: var(--iabf-ink, #1c1b1f);
}
.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
}
.back-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  margin: 16px 0 8px;
  color: var(--iabf-ink, #1c1b1f);
}
.subtitle {
  font-size: 17px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 28px;
  line-height: 1.5;
}
.package-hero {
  padding: 24px 22px;
  margin-bottom: 32px;
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
}
.package-kicker {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 8px;
}
.package-name {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 28px;
  letter-spacing: -.02em;
  margin: 0 0 8px;
  color: var(--iabf-ink, #1c1b1f);
  border: none;
  padding: 0;
}
.package-lodging {
  font-size: 16px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0;
}
.package-hint {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
  margin: 10px 0 0;
}
.section-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  margin: 0 0 14px;
  color: var(--iabf-ink, #1c1b1f);
}
.includes-group {
  margin-bottom: 20px;
}
.includes-group:last-child {
  margin-bottom: 28px;
}
.includes-group-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 8px;
  padding: 0 2px;
}
.includes-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
}
.includes-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}
.includes-row:last-child {
  border-bottom: none;
}
.includes-row:nth-child(even) {
  background: #faf9fb;
}
.includes-mark {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  margin-top: 2px;
  background: rgba(138, 149, 125, 0.15);
  color: var(--iabf-primary, #8a957d);
}
.includes-row.is-excluded .includes-mark {
  background: rgba(138, 135, 146, 0.12);
  color: var(--iabf-ink-faint, #8a8792);
}
.includes-row.is-excluded .includes-label {
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}
.includes-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.includes-label {
  font-size: 16px;
  font-weight: 500;
  color: var(--iabf-ink, #1c1b1f);
  line-height: 1.35;
}
.includes-detail {
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.4;
}
.gifts-section {
  margin-bottom: 32px;
}
.gifts-lede {
  font-size: 16px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 10px;
}
.gifts-policy {
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 16px;
}
.gifts-policy a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
}
.includes-group >>> .gifts-form--nested {
  margin-top: 12px;
}
.footer-hint {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
  margin: 0 0 32px;
}
.related-links {
  margin-top: 8px;
}
.link-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.link-list li {
  margin-bottom: 10px;
}
.link-list a {
  color: var(--iabf-primary, #8a957d);
  font-size: 17px;
  text-decoration: none;
  font-weight: 700;
}
.link-list a:hover {
  text-decoration: underline;
}
a {
  color: var(--iabf-primary, #8a957d);
}
</style>
